<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="720px"
    :fullscreen="$vuetify.display.smAndDown"
  >
    <v-card elevation="0">
      <v-card-title class="text-wrap">
        {{ $t("workshop.scanner.title") }}
      </v-card-title>
      <v-container>
        <v-row>
          <!-- CAMERA -->
          <v-col cols="12">
            <qrcode-stream
              v-if="dialog"
              :paused="paused"
              class="workshop-qr-stream"
              @decode="lookup"
              @init="onInit"
            >
              <template #loading>
                <v-progress-circular indeterminate color="primary" />
              </template>
            </qrcode-stream>
          </v-col>

          <v-col v-if="loading" cols="12" class="text-center">
            <v-progress-circular indeterminate color="primary" />
          </v-col>

          <v-col v-if="message" cols="12">
            <v-alert :type="message.type" variant="tonal" border="bottom">
              {{ $t(message.key) }}
            </v-alert>
            <v-btn
              v-if="dossierId"
              class="mt-2"
              block
              variant="outlined"
              color="primary"
              @click="openAcceptance"
            >
              {{ $t("workshop.scanner.open_acceptance") }}
            </v-btn>
          </v-col>

          <v-col cols="12">
            <div class="d-flex flex-wrap align-center ga-2">
              <v-btn variant="text" prepend-icon="mdi-refresh" @click="reScan">
                {{ $t("workshop.scanner.rescan") }}
              </v-btn>
              <v-spacer />
              <v-btn variant="outlined" color="error" @click="close">
                {{ $t("workshop.scanner.close") }}
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>
// Workshop QR scanner (Stage 3a plan §D1, DF12). Copies the camera pattern of
// VehicleEntry/qrScanner.vue (L17: that file stays untouched). Camera only, on the phone and on
// desktop: no keyboard-scanner mode. The car QR is the car id: it is checked locally (24 hex
// characters), then GET /workshop/by-car opens the checklist.
// Emits open-checklist (checklistId) and camera-unavailable (the page focuses its search).
import { QrcodeStream } from "vue3-qrcode-reader";
import { getWorkshopByCar } from "@/api/compliance";
import { isCarId, scanOutcome } from "./workshopList.mjs";

export default {
  components: {
    QrcodeStream,
  },
  emits: ["open-checklist", "camera-unavailable"],
  data: () => ({
    dialog: false,
    paused: false,
    loading: false,
    message: null,
    dossierId: null,
  }),
  methods: {
    open() {
      this.reset();
      this.dialog = true;
    },
    close() {
      this.dialog = false;
      this.reset();
    },
    reset() {
      this.paused = false;
      this.loading = false;
      this.message = null;
      this.dossierId = null;
    },
    reScan() {
      this.reset();
    },
    // Camera denied or unavailable: close and let the page offer the search.
    async onInit(promise) {
      try {
        await promise;
      } catch (err) {
        // Closed meanwhile: not a camera failure.
        if (!this.dialog) return;
        this.close();
        this.$emit("camera-unavailable");
      }
    },
    async lookup(value) {
      if (this.loading || !value) return;
      this.paused = true;
      this.dossierId = null;
      const text = String(value).trim();
      if (!isCarId(text)) {
        this.message = { type: "error", key: "workshop.scanner.invalid_code" };
        return;
      }
      this.loading = true;
      this.message = null;
      try {
        const { checklistId } = await getWorkshopByCar(text.toLowerCase());
        this.close();
        this.$emit("open-checklist", checklistId);
      } catch (err) {
        const outcome = scanOutcome(err);
        this.message = {
          type: outcome.key === "workshop.scanner.error" ? "error" : "warning",
          key: outcome.key,
        };
        this.dossierId = outcome.dossierId;
      } finally {
        this.loading = false;
      }
    },
    openAcceptance() {
      const { dossierId } = this;
      this.close();
      this.$router.push({ name: "acceptance", params: { dossierId } });
    },
  },
};
</script>

<style scoped>
.workshop-qr-stream {
  display: block;
  width: 100%;
  max-height: 55vh;
}

.workshop-qr-stream :deep(video) {
  width: 100%;
  max-height: 55vh;
  object-fit: cover;
}

@media (max-width: 959.98px) {
  .workshop-qr-stream,
  .workshop-qr-stream :deep(video) {
    max-height: calc(100dvh - 300px);
  }
}
</style>
